import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { site } from "@/data/site";

const EASE = [0.76, 0, 0.24, 1];
const BASE = 2.1; // begin after loader

const Line = ({ children, delay, className = "" }) => (
    <span className="block overflow-hidden">
        <motion.span
            className={`block ${className}`}
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.05, ease: EASE, delay }}
        >
            {children}
        </motion.span>
    </span>
);

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
    const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

    const go = (href) =>
        document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

    return (
        <section
            id="top"
            ref={ref}
            data-testid="hero-section"
            className="relative min-h-[100svh] w-full overflow-hidden bg-ivory pt-24 lg:pt-0"
        >
            <div className="mx-auto grid min-h-[100svh] max-w-[1500px] grid-cols-1 items-center gap-10 px-6 lg:grid-cols-2 lg:gap-16 lg:px-10">
                {/* Text */}
                <div className="order-2 lg:order-1">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: BASE, duration: 0.8 }}
                        className="mb-7 flex items-center gap-4"
                    >
                        <span className="h-px w-10 bg-orange" />
                        <span className="eyebrow">RERA Registered · Mumbai</span>
                    </motion.div>

                    <h1 className="font-serif text-4xl font-medium leading-[1.06] tracking-tight text-charcoal sm:text-5xl lg:text-6xl">
                        <Line delay={BASE}>
                            Every <span className="font-semibold">Great Investment</span>
                        </Line>
                        <Line delay={BASE + 0.1}>Starts With the</Line>
                        <span className="block overflow-hidden">
                            <motion.span
                                className="block italic text-orange"
                                initial={{ y: "110%" }}
                                animate={{ y: "0%" }}
                                transition={{
                                    duration: 1.05,
                                    ease: EASE,
                                    delay: BASE + 0.3,
                                }}
                            >
                                Right Address.
                            </motion.span>
                        </span>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: BASE + 0.55, duration: 0.9 }}
                        className="mt-8 max-w-lg text-base leading-relaxed text-slatey md:text-lg"
                    >
                        Discover Mumbai's most prestigious residences with trusted
                        guidance from Swastik Realty — helping you choose homes that
                        create lifestyle, legacy and long-term value.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: BASE + 0.7, duration: 0.9 }}
                        className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
                    >
                        <button
                            data-testid="hero-explore-button"
                            onClick={() => go("#signature")}
                            className="group flex items-center justify-center gap-3 bg-orange px-8 py-4 text-xs uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-orange-dark"
                        >
                            Explore Signature Collection
                            <ArrowRight
                                size={15}
                                className="transition-transform duration-300 group-hover:translate-x-1"
                            />
                        </button>
                        <button
                            data-testid="hero-consultation-button"
                            onClick={() => go("#consultation")}
                            className="flex items-center justify-center border border-charcoal/25 px-8 py-4 text-xs uppercase tracking-[0.15em] text-charcoal transition-colors duration-300 hover:border-orange hover:text-orange"
                        >
                            Book Private Consultation
                        </button>
                    </motion.div>
                </div>

                {/* Image */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: BASE - 0.2, duration: 1.1 }}
                    className="relative order-1 h-[42vh] w-full lg:order-2 lg:h-[82vh]"
                >
                    <div className="relative h-full w-full overflow-hidden">
                        <motion.img
                            style={{ y: imgY, scale }}
                            src={site.hero.image}
                            alt="Luxury Mumbai coastal residence view from a private balcony"
                            loading="eager"
                            fetchpriority="high"
                            className="absolute inset-0 h-[112%] w-full object-cover"
                        />
                    </div>
                    <div className="pointer-events-none absolute -bottom-4 -left-4 hidden h-24 w-24 border-b border-l border-champagne lg:block" />
                    <div className="pointer-events-none absolute -right-4 -top-4 hidden h-24 w-24 border-r border-t border-champagne lg:block" />
                </motion.div>
            </div>

            <motion.button
                onClick={() => go("#philosophy")}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: BASE + 1, duration: 1 }}
                aria-label="Scroll down"
                className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-slatey lg:flex"
            >
                <span className="text-[10px] uppercase tracking-luxe">Scroll</span>
                <motion.span
                    animate={{ y: [0, 7, 0] }}
                    transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
                >
                    <ArrowDown size={15} className="text-orange" />
                </motion.span>
            </motion.button>
        </section>
    );
};
